import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign } from "lucide-react";
import type { PowerWashingFormData } from "./PowerWashingProposalForm";

interface PowerWashingCostCalculatorProps {
  formData: PowerWashingFormData;
}

export function calculatePowerWashingCosts(data: PowerWashingFormData) {
  const isSqft = data.pricingMode === "sqft";
  const laborCost = isSqft
    ? (data.ratePerSqFt || 0) * (data.totalSquareFootage || 0)
    : data.laborRate * data.estimatedLaborHours;
  const lineItemTotal = data.lineItems.filter((i) => i.enabled).reduce((sum, i) => sum + i.cost, 0);
  const reclamationFee = data.waterReclamationRequired ? Math.max(75, data.totalSquareFootage * 0.02) : 0;
  const directCosts = laborCost + data.detergentCost + data.equipmentCost + lineItemTotal + data.permitCosts + reclamationFee;
  const overheadAmount = directCosts * (data.overheadPercent / 100);
  const subtotal = directCosts + overheadAmount;
  const profitAmount = subtotal * (data.profitPercent / 100);
  const totalServiceCost = subtotal + profitAmount;
  const costPerSqFt = data.totalSquareFootage > 0 ? totalServiceCost / data.totalSquareFootage : 0;

  return {
    laborCost,
    lineItemTotal,
    reclamationFee,
    directCosts,
    overheadAmount,
    subtotal,
    profitAmount,
    totalServiceCost,
    costPerSqFt,
  };
}

const fmt = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

const PowerWashingCostCalculator = ({ formData }: PowerWashingCostCalculatorProps) => {
  const costs = calculatePowerWashingCosts(formData);

  return (
    <Card className="border-border bg-card shadow-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-foreground">
          <DollarSign className="h-5 w-5 text-primary" />
          Service Cost Estimate
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border border-border bg-secondary p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Total Service Cost</p>
          <p className="text-2xl font-bold text-primary">{fmt(costs.totalServiceCost)}</p>
          {formData.totalSquareFootage > 0 && (
            <p className="text-xs text-muted-foreground mt-1">{fmt(costs.costPerSqFt)} per sq ft</p>
          )}
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between text-muted-foreground">
            <span>
              {formData.pricingMode === "sqft"
                ? `Labor (${formData.totalSquareFootage.toLocaleString()} sq ft × ${fmt(formData.ratePerSqFt)}/sq ft)`
                : `Crew Labor (${formData.estimatedLaborHours.toLocaleString()} hrs × ${fmt(formData.laborRate)}/hr)`}
            </span>
            <span className="text-foreground font-medium">{fmt(costs.laborCost)}</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>Detergent / Chemistry</span>
            <span className="text-foreground font-medium">{fmt(formData.detergentCost)}</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>Equipment / Fuel</span>
            <span className="text-foreground font-medium">{fmt(formData.equipmentCost)}</span>
          </div>
          {costs.lineItemTotal > 0 && (
            <div className="flex justify-between text-muted-foreground">
              <span>Service Breakdown</span>
              <span className="text-foreground font-medium">{fmt(costs.lineItemTotal)}</span>
            </div>
          )}
          {costs.reclamationFee > 0 && (
            <div className="flex justify-between text-muted-foreground">
              <span>Water Reclamation / EPA Compliance</span>
              <span className="text-foreground font-medium">{fmt(costs.reclamationFee)}</span>
            </div>
          )}
          {formData.permitCosts > 0 && (
            <div className="flex justify-between text-muted-foreground">
              <span>Permit / Disposal Fees</span>
              <span className="text-foreground font-medium">{fmt(formData.permitCosts)}</span>
            </div>
          )}
          <div className="border-t border-border pt-2 flex justify-between text-muted-foreground">
            <span>Direct Costs Subtotal</span>
            <span className="text-foreground font-medium">{fmt(costs.directCosts)}</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>Overhead ({formData.overheadPercent}%)</span>
            <span className="text-foreground font-medium">{fmt(costs.overheadAmount)}</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>Profit ({formData.profitPercent}%)</span>
            <span className="text-foreground font-medium">{fmt(costs.profitAmount)}</span>
          </div>
        </div>

        <div className="rounded-lg gradient-accent border border-primary/20 p-3">
          <p className="text-xs text-muted-foreground">
            (Labor + Chemistry + Equipment + Services + Reclaim + Fees) × (1 + {formData.overheadPercent}% OH) × (1 + {formData.profitPercent}% Profit) = {fmt(costs.totalServiceCost)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {formData.serviceType?.replace(/-/g, " ") || "Service"} · {formData.serviceFrequency?.replace(/-/g, " ")} · {formData.totalSquareFootage.toLocaleString()} sq ft
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PowerWashingCostCalculator;
